import { useParams } from "react-router-dom"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import ModalCheckAction from "../../../components/elements/ModalCheckAction"
import { useContext, useEffect, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getSingleOfferData } from "../api/apiCalls"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import BillFromOfferModal from "../components/billFromOffer/BillFromOfferModal"
import { useArchiveOfferMutation, useUpdateStatusOfferMutation } from "../api/services"
import { useDownloadDocumentMutation } from "../../api/services"
import ObjectDetailButtonRow from "../components/offer/OfferDetailButtonRow"
import OfferDetailHead from "../components/offer/OfferDetailHead"
import OfferDetailOfferTable from "../components/offer/OfferDetailOfferTable"
import OfferDetailFiles from "../components/offer/OfferDetailFiles"
import BasePreview from "../../../components/elements/BasePreview"
import { useResendMailMutation } from "../../../services/api/services"
import { API, GeneralQueryKeys } from "../../../api/constants"
import { SidePanelContext } from "../../../components/contexts/SidePanelContext"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseInputField from "../../../components/elements/BaseInputField"

export default function OfferDetailPage() {
    const { id } = useParams<string>()
    const { data, error, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.OFFER_SINGLE + id],
        queryFn: () => getSingleOfferData(id || ""),
    })

    // preview offer or attachements
    const sidePanelCon = useContext(SidePanelContext)
    const [previewElement, setpreviewElement] = useState<string>()
    const handleChangePreviewElement = (s3Key: string) => {
        if (previewElement === API + `/offer/${data?._id}/download?s3Key=${s3Key}`) {
            sidePanelCon?.trigger(3)
        } else {
            setpreviewElement(API + `/offer/${data?._id}/download?s3Key=${s3Key}`)
        }
    }
    const modalCon = useContext(ModalContext)
    useEffect(() => {
        modalCon?.onScreenChangeCloseAllModals()
        if (previewElement) {
            sidePanelCon?.trigger(3)
        }
    }, [previewElement])

    // UPDATE REQUESTS - Offer Status & Archived
    const queryClient = useQueryClient()
    const [status, setstatus] = useState<"unanswered" | "settled" | "rejected" | string>(data?.status)
    useEffect(() => {
        if (data?.status) {
            setstatus(data?.status)
        }
    }, [data?.status])

    const { mutate: updateStatusOffer } = useUpdateStatusOfferMutation(queryClient)
    const handleChangeStatus = () => {
        updateStatusOffer({
            id: data?.id ?? id,
            body: { status: status },
        })
    }

    const { mutate: archiveOffer } = useArchiveOfferMutation(queryClient, modalCon, 0)
    const handleArchiveOffer = () => {
        archiveOffer({
            id: data?._id ?? id,
            body: { archived: !data?.archived },
        })
    }

    // download offer file - or offer attachments
    const { mutate: downloadFile } = useDownloadDocumentMutation()
    const handleDownload = (fileData?: { s3Key: string; filename: string }) => {
        if (fileData) {
            downloadFile({ id: data._id, s3Key: fileData.s3Key, filetype: "offer", filename: fileData.filename })
        } else {
            downloadFile({ id: data._id, s3Key: data.s3DocumentRef.s3Key, filetype: "offer", filename: "Angebot " + data.nr + ".pdf" })
        }
    }

    const [resendUri, setResendUri] = useState<string>()
    const [mailPreviewContent, setMailPreviewContent] = useState<string>()
    const [resenEmailAddress, setresenEmailAddress] = useState<string>()
    const { mutate: resendMailFunction } = useResendMailMutation()
    const handleResendMail = (e: string) => {
        const resendBody = {
            mailReceivers: resenEmailAddress ? [resenEmailAddress] : undefined,
            mailReportId: data?.mailReports[0]?._id,
        }
        resendMailFunction({ path: e, body: resendBody })
    }

    return (
        <BasePage>
            <BaseHeadBanner title={"Angebot " + data?.nr} />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !isLoading && !error && (
                <BaseGrowBody classProps="overflow-x-hidden overflow-y-scroll   ">
                    <OfferDetailHead data={data} setResendUri={setResendUri} setMailPreviewContent={setMailPreviewContent} />
                    <OfferDetailOfferTable data={data} />
                    <OfferDetailFiles data={data} handleDownload={handleDownload} handleChangePreviewElement={handleChangePreviewElement} />
                    <ObjectDetailButtonRow
                        status={status}
                        setstatus={setstatus}
                        statusCopy={data.status}
                        handleChangeStatus={handleChangeStatus}
                        createdBillCount={data.created_bill.length}
                        handleDownload={handleDownload}
                        handleChangePreviewElement={() => handleChangePreviewElement(data?.s3DocumentRef?.s3Key)}
                    />
                </BaseGrowBody>
            )}
            <ModalCheckAction
                modalId={0}
                headline={data?.archived ? "Archivieren Rückgängig machen?" : "Wirklich Archivieren?"}
                text={data?.archived ? "Sind Sie sicher, dass Sie das Angebot nicht mehr archivieren wollen?" : "Sind Sie sich sicher, dass Sie das Angebot archivieren wollen?"}
                buttonText={data?.archived ? "Rückgängig" : "Archivieren"}
                buttonColor={"bg-blue-300 hover:bg-blue-400 border-none"}
                func={handleArchiveOffer}
            />
            <BillFromOfferModal
                documents={data?.documents}
                customer={{
                    _id: data?.customer_id?._id,
                    name: data?.customer_id?.customer?.name,
                }}
                object={{
                    _id: data?.object_id?._id,
                    adress: data?.object_id?.adress?.street + " " + data?.object_id?.adress?.nr + ", " + data?.object_id?.adress?.city + " " + data?.object_id?.adress?.plz,
                }}
                positions={data?.positions}
                offer={id ?? data?.id}
            />
            <BasePreview title={"Angebotsvorschau"} sidePanelId={3} previewType={"document"} content={previewElement} />
            <BasePreview
                title={"Mailvorschau"}
                sidePanelId={4}
                previewType={"mail"}
                content={mailPreviewContent ?? ""}
                resendFunction={status !== "rejected" ? () => handleResendMail(resendUri ?? "") : undefined}
                customSendSection={
                    <div className="flex flex-col h-fit max-w-[32rem]">
                        <BaseInputField
                            label={
                                "Sie haben die Möglichkeit, eine alternative E-Mail anzugeben, welche die Mail erhalten soll. Falls Das Feld leer bleibt, wird die Mail an die alten Empfänger zugestellt."
                            }
                            type={"mail"}
                            placeholder="max@muster.de"
                            onChange={setresenEmailAddress}
                        />
                    </div>
                }
            />
        </BasePage>
    )
}
